import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';
import { Formik, type FormikHelpers } from 'formik';
import dayjs from 'dayjs';
import { Loading } from 'pages';
import { cn, useValidatePhoneNumber } from 'utils/helpers';

import { useGetBookingEvent } from '../../hooks';
import ClientBookingForm from './ClientBookingForm';
import {
  CLIENT_BOOKING_FORM,
  CLIENT_BOOKING_SCHEMA,
  CLIENT_BOOKING_STEPS,
} from '../../forms/schema';
import ClientBookingReview from './ClientBookingReview';
import ClientBookingSuccess from './ClientBookingSuccess';
import ClientBookingLinkExpired from './ClientBookingLinkExpired';
import useCreateBookingEvent from '../../hooks/useCreateBookingEvent';

const FORM_INIT = {
  firstName: '',
  lastName: '',
  contactNumber: '',
  emailAddress: '',
  selectedDate: null,
  startAt: null,
};

export type BOOKING_DETAILS = {
  timezone: string;
  title: string;
  content?: string | null;
  organizer: string;
  duration: number;
  eventUrl: string;
  emailAddress: string;
  startAt?: string;
  endAt?: string;
  eventId: string;
};

export default function ClientBooking() {
  const { id } = useParams();
  const { data, isLoading, isError } = useGetBookingEvent(id as string);

  const { mutate: createBookingEvent } = useCreateBookingEvent();
  const { mutate: validatePhoneNumber } = useValidatePhoneNumber();
  const [bookingDetails, setBookingDetails] = useState<BOOKING_DETAILS>({
    title: '',
    content: '',
    organizer: '',
    duration: 0,
    eventUrl: '',
    emailAddress: '',
    eventId: '',
    timezone: '',
  });

  const [step, setStep] = useState<
    (typeof CLIENT_BOOKING_STEPS)[keyof typeof CLIENT_BOOKING_STEPS] | null
  >(null);

  useEffect(() => {
    if (isError) {
      setStep(CLIENT_BOOKING_STEPS.expired);
    }
  }, [isError]);

  useEffect(() => {
    if (data) {
      setStep(CLIENT_BOOKING_STEPS.form);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      setBookingDetails((prevVal) => ({
        ...prevVal,
        title: data?.title,
        content: data?.content,
        organizer: data?.accountUserId.fullName,
        duration: data?.duration,
        eventUrl: data?.eventUrl,
        emailAddress: data?.accountUserId.emailAddress,
        timezone: data?.timezone,
      }));
    }
  }, [data]);

  const componentToRender = () => {
    switch (step) {
      case CLIENT_BOOKING_STEPS.form:
        return <ClientBookingForm bookingDetails={bookingDetails} />;
      case CLIENT_BOOKING_STEPS.review:
        return (
          <ClientBookingReview
            setStep={setStep}
            bookingDetails={bookingDetails}
          />
        );
      case CLIENT_BOOKING_STEPS.success:
        return <ClientBookingSuccess bookingDetails={bookingDetails} />;
      case CLIENT_BOOKING_STEPS.expired:
        return (
          <ClientBookingLinkExpired
            emailAddress={bookingDetails.emailAddress}
          />
        );
      default:
        return <Loading />;
    }
  };

  if (isLoading) return <Loading />;

  /**
   * Submits the form to create the client booking
   * @param {typeof FORM_INIT} value
   * @param {import('formik').FormikHelpers} formikHelpers
   * @return {void | Promise<any>}
   */
  const onSubmit = (
    value: CLIENT_BOOKING_FORM,
    { setSubmitting, setFieldError }: FormikHelpers<CLIENT_BOOKING_FORM>,
  ): void | Promise<any> => {
    const castValues = CLIENT_BOOKING_SCHEMA.cast(value);
    if (step === CLIENT_BOOKING_STEPS.form) {
      if (castValues.contactNumber) {
        validatePhoneNumber(castValues.contactNumber, {
          onSuccess({ data: validatePhoneNumberData }) {
            if (!validatePhoneNumberData.valid) {
              setFieldError('contactNumber', 'Please put a valid phone number');
            } else {
              setStep(CLIENT_BOOKING_STEPS.review);
            }
            setSubmitting(false);
          },
        });
      } else {
        setStep(CLIENT_BOOKING_STEPS.review);
        setSubmitting(false);
      }
    } else if (step === CLIENT_BOOKING_STEPS.review) {
      setSubmitting(true);
      createBookingEvent(
        {
          id: id as string,
          payload: {
            date: dayjs.unix(value.startAt?.unix as number).toISOString(),
            emailAddress: castValues.emailAddress,
            firstName: castValues.firstName,
            lastName: castValues.lastName,
            contactNumber: castValues.contactNumber,
            requestingTimezone: value.startAt?.convertedTimezone,
          },
        },
        {
          onSettled: () => setSubmitting(false),
          onError: () => setStep(CLIENT_BOOKING_STEPS.form),
          onSuccess({ data: res }) {
            setStep(CLIENT_BOOKING_STEPS.success);
            setBookingDetails((prevVal) => ({
              ...prevVal,
              eventUrl: res.eventUrl,
              eventId: res.id,
              startAt: res.startAt,
              endAt: res.endAt,
            }));
          },
        },
      );
    }
  };

  return (
    <div
      className={cn(
        'bg-[#F8FBFD]',
        'w-full',
        'grid',
        'place-items-center',
        'min-h-screen',
        'py-[45px]',
        'px-5',
      )}
    >
      <Formik<CLIENT_BOOKING_FORM>
        enableReinitialize
        initialValues={FORM_INIT}
        onSubmit={onSubmit}
        validationSchema={CLIENT_BOOKING_SCHEMA}
      >
        {componentToRender}
      </Formik>
    </div>
  );
}
